import { Controller, useFormContext } from 'react-hook-form';
import { validateProps } from '../../../../../utils/propValidator';
import DateTimeInput from './DateTimeInput';

const propRules = {
  id: { type: 'string', required: true },
  name: { type: 'string', required: true },
  label: { type: 'string' },
  rules: { type: 'object' },
  disabled: { type: 'boolean' },
  disablePast: { type: 'boolean' },
  minDate: { type: 'object' },
  views: { type: 'array' },
  format: { type: 'string' },
  showErrorInline: { type: 'boolean' },
  helperText: { type: 'string' },
  orientation: { oneOf: ['row', 'column'] },
  sx: { type: 'object' },
};

/**
 * ControlledDateTimeInput
 *
 * React Hook Form adapter for DateTimeInput.
 * This component:
 * - Binds DateTimeInput to RHF via Controller
 * - Maps RHF error state to inline or external error display
 * - Does not implement any date/time logic itself
 *
 * @param {Object} props
 * @param {string} props.id - Input id
 * @param {string} props.name - Form field name
 * @param {string} [props.label] - Field label
 * @param {Object} [props.rules] - RHF validation rules
 * @param {boolean} [props.disabled] - Disable input
 * @param {boolean} [props.disablePast] - Disable past dates
 * @param {dayjs.Dayjs} [props.minDate] - Minimum selectable date
 * @param {Array<'day'|'month'|'year'>} [props.views] - Date picker views
 * @param {string} [props.format] - Date display format
 * @param {boolean} [props.showErrorInline=true] - Whether to render error inline
 * @param {string} [props.helperText] - Helper text (character count style)
 * @param {'row'|'column'} [props.orientation] - Layout orientation
 * @param {Object} [props.sx] - Wrapper sx styles
 * @returns {React.ReactElement}
 */
export default function ControlledDateTimeInput(props: any) {
  validateProps(props as Record<string, unknown>, propRules, 'ControlledDateTimeInput');

  const {
    id,
    name,
    label,
    rules,
    disabled,
    disablePast,
    minDate,
    views,
    format,
    showErrorInline = true,
    helperText,
    orientation,
    sx = {},
  } = props;

  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <DateTimeInput
          id={id}
          name={name}
          label={label}
          value={field.value ?? null}
          onChange={field.onChange}
          disabled={disabled}
          disablePast={disablePast}
          minDate={minDate}
          views={views}
          format={format}
          error={Boolean(fieldState.error)}
          errorMessage={fieldState.error?.message}
          showErrorInline={showErrorInline}
          helperText={helperText}
          orientation={orientation}
          sx={sx}
        />
      )}
    />
  );
}
